import { useMemo } from 'react';
import { OptionKey, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT } from '@/common/constants';
import { API_URL } from '@/dtos/generated-paths';
import type { IPersonSpendLimitDto } from '@/dtos/models';

const QUERY_KEY = 'useGetPersonSpendLimitsQueryKey';

export interface PersonSpendLimit {
  id: number;
  policyId: number;
  employeeId: number;
  dependentId: number | null;
  personSpendLimit: number;
  totalSpent: number;
}

const getFormattedData = (data: IPersonSpendLimitDto[]): PersonSpendLimit[] => {
  return data.map((limit) => ({
    id: limit.id || 0,
    policyId: limit.policy_id || 0,
    employeeId: limit.employee_id || 0,
    dependentId: limit.dependent_id || null,
    personSpendLimit: limit.person_spend_limit || 0,
    totalSpent: limit.total_spent || 0,
  }));
};

interface Props {
  accountId?: string;
  periodFrom?: OptionKey;
  enabled?: boolean;
}

const useGetPersonSpendLimitsQuery = ({ accountId, periodFrom, enabled = true }: Props) => {
  const { data, isLoading, isSuccess } = useQuery(
    [QUERY_KEY, accountId, periodFrom],
    () => {
      return api.get<IPersonSpendLimitDto[]>(API_URL.PERSON_SPEND_LIMITS, {
        params: {
          account_id: accountId,
          period_from: dayjs(periodFrom).format(DEFAULT_DATE_FORMAT),
        },
      });
    },
    {
      enabled: enabled && Boolean(accountId) && Boolean(periodFrom),
    }
  );

  const formattedData = useMemo(() => getFormattedData(data?.data || []), [data]);

  return {
    personSpendLimits: formattedData,
    isSuccess,
    isLoading,
  };
};

export default useGetPersonSpendLimitsQuery;
